import type { FunctionComponent } from "react";
import { useState } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import type { Dispatch } from "redux";
import { connect } from "react-redux";

import { Button, ButtonLabel, PopupClickContainerStateless, FilterNotification } from "@similarweb/ui-components";

import { onFilterChangedEducationalTooltipPresented } from "app/actions/usersFiltersSelectionActions";
import {
    Container,
    FilterNotificationWrapper,
    Footer,
    Text,
    Title,
    TitleContainer,
} from "pro-features/components/tooltips/src/filterChangedTooltip/FilterChangedTooltipStyles";
import type { IFilterChangedNotificationProps } from "pro-features/components/tooltips/src/filterChangedTooltip/FilterChangedTooltipTypes";
import { useTranslation } from "pro-features/components/WithTranslation/src/I18n";
import { buttonTypes } from "app/UtilitiesAndConstants/Constants/ButtonTypes";
import { allTrackers } from "app/services/track/track";

const FilterChangedTooltipContent: FunctionComponent<any> = ({ title, text, onClose, shouldPresentFooter }) => {
    const translate = useTranslation();
    const onWrapperClick = (event: FixmeType) => {
        event.stopPropagation();
    };

    return (
        <Container onClick={onWrapperClick}>
            {title ? (
                <TitleContainer>
                    <Title>{title}</Title>
                </TitleContainer>
            ) : null}
            <Text>{text}</Text>
            {shouldPresentFooter ? (
                <Footer>
                    <Button
                        type={buttonTypes.INVERTED}
                        onClick={onClose}
                    >
                        <ButtonLabel>{translate("modal.no-touch.not-allowed.button")}</ButtonLabel>
                    </Button>
                </Footer>
            ) : null}
        </Container>
    );
};

const FilterChangedNotification: FunctionComponent<IFilterChangedNotificationProps> = (props) => {
    const {
        filterChangeEducationalTooltipPresented,
        button,
        onClose,
        width = 412,
        tooltipBody,
        filterType,
        onClick,
    } = props;
    const translate = useTranslation();
    const [isOpen, setIsOpen] = useState<boolean>(!filterChangeEducationalTooltipPresented);
    const [shouldPresentEducationalTooltip, setShouldPresentEducationalTooltip] = useState<boolean>(false);
    const { state } = useCurrentStateAndParams();
    const {
        filterConsistencyTooltipTextKey: tooltipTextKey = "common.filters.tooltip.educational.body",
        filterConsistencyTooltipTitleKey: tooltipTitleKey = "common.filters.tooltip.educational.header",
    } = state;
    if (!filterChangeEducationalTooltipPresented && isOpen && !shouldPresentEducationalTooltip) {
        allTrackers.dispatchTrackEvent(
            "Change Query Filter",
            "open",
            `Change Query Filter/Got It Pop Up/${filterType || ""}`,
        );
        props.onFilterChangedEducationalTooltipPresented();
        setShouldPresentEducationalTooltip(true);
    }

    const onCloseClick = (event: FixmeType) => {
        event.stopPropagation();
        setShouldPresentEducationalTooltip(false);
        if (event) {
            setIsOpen(false);
        }
        allTrackers.dispatchTrackEvent(
            "Change Query Filter",
            "close",
            `Change Query Filter/Pop Up/${filterType || ""}`,
        );
        onClose?.();
    };
    const popupConfig = {
        placement: "bottom",
        width,
        defaultOpen: true,
        cssClassContainer: !shouldPresentEducationalTooltip
            ? "Popup-element-wrapper--dark-mode"
            : "Popup-element-wrapper--notification-mode",
        cssClassContent: "Popup-content-no-padding",
        enabled: true,
    };
    const tooltipTitle = shouldPresentEducationalTooltip ? translate(tooltipTitleKey) : "";
    const tooltipTextBody = shouldPresentEducationalTooltip ? translate(tooltipTextKey) : tooltipBody;
    const onMouseEnter = () => {
        if (!shouldPresentEducationalTooltip) {
            setIsOpen(true);
            allTrackers.dispatchTrackEvent("Change Query Filter", "open", `Blue Dot/Appear/${filterType || ""}`);
        }
    };
    const onMouseLeave = () => {
        if (!shouldPresentEducationalTooltip) {
            setIsOpen(false);
        }
    };

    return (
        <div onClick={onClick}>
            <PopupClickContainerStateless
                content={() => (
                    <FilterChangedTooltipContent
                        title={tooltipTitle}
                        button={button}
                        onClose={onCloseClick}
                        text={tooltipTextBody}
                        shouldPresentFooter={shouldPresentEducationalTooltip}
                    />
                )}
                config={popupConfig}
                isOpen={isOpen}
                appendTo={"body"}
            >
                <FilterNotificationWrapper
                    onMouseEnter={onMouseEnter}
                    onMouseLeave={onMouseLeave}
                >
                    <FilterNotification
                        width={"10px"}
                        height={"10px"}
                        animationOn={shouldPresentEducationalTooltip}
                    />
                </FilterNotificationWrapper>
            </PopupClickContainerStateless>
        </div>
    );
};
const mapStateToProps = (state: FixmeType) => {
    const { filterChangeEducationalTooltipPresented } = state.usersFiltersSelection;

    return { filterChangeEducationalTooltipPresented };
};

const mapDispatchToProps = (dispatch: Dispatch) => {
    return {
        onFilterChangedEducationalTooltipPresented: () => {
            dispatch(onFilterChangedEducationalTooltipPresented());
        },
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(FilterChangedNotification);
